// The late-payment interest calculator on /features/late-payment-interest/.
// A thin DOM layer over shared/statutory-interest.js — the SAME module the
// product computes a real interest invoice with, bundled in by
// scripts/build-web.mjs (the demo-intercept pattern), so the page can never
// quote a different figure than the app charges.
import { computeStatutoryInterest } from "../../shared/statutory-interest.js";
import { formatDocumentDate } from "../../shared/document-format.js";
import { readMoneyText } from "../../shared/money-input.js";

const gbp = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });

function init() {
  const form = document.getElementById("interest-calc");
  if (!form) return;
  const amountEl = document.getElementById("calc-amount");
  const dueEl = document.getElementById("calc-due");
  const out = {
    days: document.getElementById("calc-days"),
    base: document.getElementById("calc-base-rate"),
    rate: document.getElementById("calc-rate"),
    interest: document.getElementById("calc-interest"),
    daily: document.getElementById("calc-daily"),
    compensation: document.getElementById("calc-compensation"),
    total: document.getElementById("calc-total"),
    note: document.getElementById("calc-note")
  };

  // A sensible starting point: due 30 days ago, so the first paint shows the
  // maths working rather than a row of zeros.
  if (dueEl && !dueEl.value) {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    dueEl.value = d.toISOString().slice(0, 10);
  }

  function recalc() {
    // The product's own rule for a typed amount (shared/money-input.js).
    const typed = readMoneyText(amountEl.value).pence;
    const amountPence = typed != null && typed > 0 ? typed : 0;
    // The rate is the one the law fixes for a debt due that day: the base
    // rate on the 30 June or 31 December before its interest starts, from
    // the Bank of England's record (shared/base-rate-history.js).
    const { days, interestPence, compensationPence, ratePercent, baseRatePercent, referenceDate } = computeStatutoryInterest({
      outstandingPence: amountPence,
      debtPence: amountPence,
      dueDate: dueEl.value,
      // The visitor's own day: a marketing calculator, belonging to no company.
      today: new Date()
    });
    // Shown even before the due date passes: "what this starts costing per
    // day" is the number that gets a late payer moving.
    const dailyPence = Math.round((amountPence * (ratePercent / 100)) / 365);

    out.days.textContent = String(days);
    out.base.textContent = referenceDate ? `${baseRatePercent}% on ${formatDocumentDate(referenceDate)}` : "–";
    out.rate.textContent = referenceDate ? `${ratePercent}% a year` : "–";
    out.daily.textContent = gbp.format(dailyPence / 100);
    out.interest.textContent = gbp.format(interestPence / 100);
    out.compensation.textContent = gbp.format(compensationPence / 100);
    out.total.textContent = gbp.format((interestPence + compensationPence) / 100);
    if (out.note) {
      out.note.hidden = !(amountPence > 0 && days === 0);
    }
  }

  for (const el of [amountEl, dueEl]) {
    el.addEventListener("input", recalc);
    el.addEventListener("change", recalc);
  }
  recalc();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
